import React from "react";
import { useDarkMode } from "../../contexts/DarkModeContext";

// Inlined styles to replace missing hrmStyles.ts
const hrmGridStyles = {
    stats: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
        gap: "24px",
    } as React.CSSProperties,
};

const getStatCardStyles = (isDarkMode: boolean): React.CSSProperties => ({
    backgroundColor: isDarkMode ? "#1e293b" : "#ffffff",
    padding: "24px",
    borderRadius: "12px",
    border: isDarkMode ? "1px solid #334155" : "1px solid #E5E7EB",
    boxShadow: isDarkMode ? "0 1px 2px 0 rgba(0, 0, 0, 0.2)" : "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
});

const getCompactStatCardStyles = (isDarkMode: boolean): React.CSSProperties => ({
    // Flat / minimal tile to avoid “card” bulk
    backgroundColor: isDarkMode ? "rgba(15, 23, 42, 0.55)" : "rgba(255, 255, 255, 0.85)",
    padding: "8px 10px",
    borderRadius: "10px",
    border: "none",
    boxShadow: "none",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
});

const createIconBadgeStyles = (color: string, isDarkMode: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    backgroundColor: isDarkMode ? `${color}30` : `${color}15`,
    color: color,
    fontSize: "24px",
});

const createCompactIconBadgeStyles = (color: string, isDarkMode: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    borderRadius: "999px",
    backgroundColor: isDarkMode ? `${color}22` : `${color}12`,
    color: color,
    fontSize: "16px",
});

export type HrmStatCard = {
    id: string;
    label: string;
    value: string | number;
    description?: string;
    accentColor: string;
    icon: React.ReactNode;
};

export type HrmStatCardsProps = {
    stats?: HrmStatCard[];
    variant?: "default" | "compact";
};

const HrmStatCards: React.FC<HrmStatCardsProps> = ({ stats = [], variant = "default" }) => {
    const { isDarkMode } = useDarkMode();
    const gridStyles: React.CSSProperties = variant === "compact"
        ? { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "10px" }
        : hrmGridStyles.stats;
    const cardStyle = variant === "compact" ? getCompactStatCardStyles(isDarkMode) : getStatCardStyles(isDarkMode);
    const labelColor = isDarkMode ? "#94a3b8" : "#6B7280";
    const valueColor = isDarkMode ? "#f1f5f9" : "#111827";
    const descColor = isDarkMode ? "#94a3b8" : "#6B7280";

    return (
        <div style={gridStyles}>
            {stats.map((stat) => (
                <div key={stat.id} style={cardStyle}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: variant === "compact" ? "4px" : "6px" }}>
                            <p style={{ fontSize: variant === "compact" ? "10px" : "14px", fontWeight: 800 as any, color: labelColor, margin: 0, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                                {stat.label}
                            </p>
                            <p style={{ fontSize: variant === "compact" ? "20px" : "36px", fontWeight: 850 as any, color: valueColor, margin: 0, lineHeight: 1.05 }}>
                                {stat.value}
                            </p>
                        </div>
                        <span style={variant === "compact" ? createCompactIconBadgeStyles(stat.accentColor, isDarkMode) : createIconBadgeStyles(stat.accentColor, isDarkMode)}>{stat.icon}</span>
                    </div>
                    {stat.description && (
                        <p style={{ fontSize: variant === "compact" ? "12px" : "14px", color: descColor, margin: 0 }}>
                            {stat.description}
                        </p>
                    )}
                </div>
            ))}
        </div>
    );
};

export default HrmStatCards;
